import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Music } from "lucide-react";

import skzImage from "@/assets/skz-study-session-1.jpg";
import itzyImage from "@/assets/itzy-group-photo-1.webp";
import blackpinkImage from "@/assets/blackpink-group-photo-1.jpg";
import aespaImage from "@/assets/aespa-group-photo-1.webp";
import twiceImage from "@/assets/kpop-study-hero.jpg";
import ateezImage from "@/assets/kpop-study-desk.jpg";

interface KpopGroup {
  name: string;
  fandom: string;
  path: string;
  image?: string;
}

const kpopGroups: KpopGroup[] = [
  { name: "Stray Kids", fandom: "STAY", path: "/study-with-skz/", image: skzImage },
  { name: "ITZY", fandom: "MIDZY", path: "/study-with-itzy/", image: itzyImage },
  { name: "Blackpink", fandom: "BLINK", path: "/study-with-blackpink/", image: blackpinkImage },
  { name: "aespa", fandom: "MY", path: "/study-with-aespa/", image: aespaImage },
  { name: "TWICE", fandom: "ONCE", path: "/study-with-twice/", image: twiceImage },
  { name: "ATEEZ", fandom: "ATINY", path: "/study-with-ateez/", image: ateezImage },
  { name: "BTS", fandom: "ARMY", path: "/study-with-bts/" },
  { name: "ENHYPEN", fandom: "ENGENE", path: "/study-with-enhypen/" },
  { name: "NCT", fandom: "NCTzen", path: "/study-with-nct/" },
  { name: "TXT", fandom: "MOA", path: "/study-with-txt/" },
  { name: "SEVENTEEN", fandom: "CARAT", path: "/study-with-seventeen/" },
  { name: "EXO", fandom: "EXO-L", path: "/study-with-exo/" },
  { name: "MONSTA X", fandom: "Monbebe", path: "/study-with-monsta-x/" },
  { name: "THE BOYZ", fandom: "THE B", path: "/study-with-the-boyz/" },
  { name: "TREASURE", fandom: "TEUME", path: "/study-with-treasure/" },
  { name: "GOT7", fandom: "Ahgase", path: "/study-with-got7/" },
  { name: "SF9", fandom: "FANTASY", path: "/study-with-sf9/" },
  { name: "LOONA", fandom: "Orbit", path: "/study-with-loona/" },
  { name: "Red Velvet", fandom: "ReVeluv", path: "/study-with-red-velvet/" },
  { name: "MAMAMOO", fandom: "MooMoo", path: "/study-with-mamamoo/" },
];

interface KpopCategoryNavProps {
  currentGroup?: string;
}

export const KpopCategoryNav = ({ currentGroup }: KpopCategoryNavProps) => {
  const otherGroups = kpopGroups.filter(group => group.name !== currentGroup);

  return (
    <section className="py-20 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-background to-muted/30" />
      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto space-y-10">
          <div className="text-center space-y-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
              <Music className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-primary">STUDY WITH K-POP</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground">
              Explore More <span className="text-gradient">K-Pop Study Rooms</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Discover study rooms for other K-Pop groups and find your perfect study atmosphere.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {otherGroups.slice(0, 10).map((group) => (
              <Link 
                key={group.name} 
                to={group.path}
                className="group"
              >
                <Card className="overflow-hidden border-border/50 hover:border-primary/50 transition-all hover:scale-105 hover:shadow-lg">
                  <div className="relative aspect-[4/3]">
                    {group.image ? (
                      <img 
                        src={group.image} 
                        alt={`Study with ${group.name}`}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                        <Music className="w-8 h-8 text-primary" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-3 text-center">
                      <h3 className="font-display text-sm font-semibold text-white group-hover:text-primary transition-colors">
                        {group.name}
                      </h3>
                      <p className="text-xs text-white/70 mt-0.5">
                        {group.fandom}
                      </p>
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>

          <div className="text-center">
            <Link 
              to="/study-with-kpop/" 
              className="inline-flex items-center gap-2 text-primary hover:underline font-medium"
            >
              View all K-Pop study rooms →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
